import React, { useEffect } from 'react';
import { Form, InputNumber, theme } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
// Removed InsightEvent import - using raw data instead
import { InsightType, InsightStatus, CurrencySymbol } from '@/constants/enums';
import { EPButton } from '@/components';
import EPInput from '@/comp/EPInput';
import EPTextArea from '@/comp/EPTextArea';
import EPSelect, { EPSelectOption } from '@/components/EPSelect/EPSelect';
import { useStores } from '@/hooks/use-stores';
import * as S from './PublishInsightModal.styles';
import { toCurrencySymbol } from '@/utils/currency.utils';

const { useToken } = theme;

interface PublishInsightModalProps {
  visible: boolean;
  insight: any | null; // Raw insight data
  onCancel: () => void;
  onPublish: (values: PublishInsightFormValues) => Promise<void>;
  isLoading?: boolean;
}

export interface PublishInsightFormValues {
  name: string;
  deviceId: string;
  description: string;
  value: number;
  currency: string;
  valueType: InsightType;
  status: InsightStatus;
}

const PublishInsightModal: React.FC<PublishInsightModalProps> = observer(
  ({ visible, insight, onCancel, onPublish, isLoading = false }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const [form] = Form.useForm<PublishInsightFormValues>();
    const { devicesStore } = useStores();

    useEffect(() => {
      if (visible && insight) {
        const rawCurrency = insight.currency || CurrencySymbol.USD;
        const initialCurrency = toCurrencySymbol(rawCurrency);

        // Use deviceId directly (not device name)
        const assetDeviceId = insight.deviceId || '';

        const formValues = {
          name: insight.name,
          deviceId: assetDeviceId,
          description: insight.description,
          value: insight.monetizedAnnualValue,
          currency: initialCurrency,
          valueType: insight.valueType,
          status: InsightStatus.Open, // Always set to Open when publishing
        };

        form.setFieldsValue(formValues);
      }
    }, [visible, insight, form]);

    const handlePublish = async () => {
      try {
        const values = await form.validateFields();
        await onPublish(values);
        form.resetFields();
      } catch (error) {
        console.error('Validation failed:', error);
      }
    };

    const handleCancel = () => {
      form.resetFields();
      onCancel();
    };

    const valueTypeOptions = Object.values(InsightType).map(type => (
      <EPSelectOption key={type} value={type}>
        {t(type.charAt(0).toUpperCase() + type.slice(1))}
      </EPSelectOption>
    ));

    // Get all station devices for asset select
    const deviceOptions = Array.isArray(devicesStore?.devicesMetadata)
      ? devicesStore.devicesMetadata.map(device => (
          <EPSelectOption key={device.id} value={device.id}>
            {device.name}
          </EPSelectOption>
        ))
      : [];

    return (
      <S.StyledModal
        title={t('Review & Publish Insight', 'Review & Publish Insight')}
        open={visible}
        onCancel={handleCancel}
        width="30%"
        style={{ top: '6%', padding: '20px 0px' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: token.marginSM }}>
            <EPButton onClick={handleCancel}>{t('Cancel', 'Cancel')}</EPButton>
            <EPButton type="primary" onClick={handlePublish} loading={isLoading}>
              {t('Publish to Customer', 'Publish to Customer')}
            </EPButton>
          </div>
        }
      >
        <Form form={form} layout="vertical" size="middle">
          <Form.Item
            name="name"
            label={t('Name', 'Name')}
            rules={[{ required: true, message: t('Please enter insight name') }]}
            style={{ marginBottom: token.marginXS }}
          >
            <EPInput placeholder={t('Enter insight name')} />
          </Form.Item>

          <Form.Item name="deviceId" label={t('Asset', 'Asset')} style={{ marginBottom: token.marginXS }}>
            <EPSelect placeholder={t('Select asset (optional for station level insights)')} size="middle" allowClear>
              {deviceOptions}
            </EPSelect>
          </Form.Item>

          <Form.Item
            name="description"
            label={t('Description', 'Description')}
            rules={[{ required: true, message: t('Please enter description') }]}
            style={{ marginBottom: token.marginXS }}
          >
            <EPTextArea rows={4} placeholder={t('Enter description')} />
          </Form.Item>

          <Form.Item
            name="value"
            label={t('Value', 'Value')}
            rules={[
              { required: true, message: t('Please enter value') },
              {
                type: 'number',
                message: t('Value must be a number'),
              },
              {
                validator: (_, value) => {
                  if (value === null || value === undefined || value === '') {
                    return Promise.resolve();
                  }
                  if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('Value must be a valid number')));
                },
              },
            ]}
            style={{ marginBottom: token.marginXS }}
          >
            <InputNumber
              style={{ width: '100%' }}
              size="middle"
              placeholder={t('Enter value')}
              min={0}
              precision={0}
              controls={false}
              parser={value => {
                if (!value) return null;
                // Remove all non-numeric characters
                const parsed = value.replace(/[^\d]/g, '');
                return parsed ? +parsed : null;
              }}
              formatter={value => {
                if (!value) return '';
                return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }}
              onKeyPress={e => {
                // Only allow numeric keys and control keys
                if (
                  !/[0-9]/.test(e.key) &&
                  !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(
                    e.key,
                  ) &&
                  !(e.ctrlKey || e.metaKey)
                ) {
                  e.preventDefault();
                }
              }}
              addonBefore={
                <Form.Item
                  name="currency"
                  noStyle
                  initialValue={toCurrencySymbol(insight?.currency)}
                  getValueFromEvent={value => value}
                >
                  <EPSelect
                    key={`currency-select-${insight?.currency || CurrencySymbol.USD}`}
                    style={{ width: 75, minWidth: 50 }}
                    size="middle"
                    showSearch={false}
                    dropdownMatchSelectWidth={false}
                    bordered={false}
                    optionLabelProp="label"
                    disabled //disabled since currency is not editable for now
                  >
                    {Object.keys(CurrencySymbol).map(currency => (
                      <EPSelectOption key={currency} value={currency} label={currency}>
                        <span style={{ fontSize: token.fontSizeLG }}>{currency}</span> {CurrencySymbol[currency]}
                      </EPSelectOption>
                    ))}
                  </EPSelect>
                </Form.Item>
              }
            />
          </Form.Item>

          <Form.Item
            name="valueType"
            label={t('Value Type', 'Value Type')}
            rules={[{ required: true, message: t('Please select value type') }]}
            style={{ marginBottom: token.marginSM }}
          >
            <EPSelect placeholder={t('Select value type')} size="middle">
              {valueTypeOptions}
            </EPSelect>
          </Form.Item>

          <Form.Item name="addFiles" label={t('Add Files', 'Add Files')} style={{ marginBottom: token.marginSM }}>
            <EPButton disabled>{t('Add Files', 'Add Files')}</EPButton>
          </Form.Item>

          <Form.Item name="status" hidden initialValue={InsightStatus.Open}>
            <input type="hidden" />
          </Form.Item>
        </Form>
      </S.StyledModal>
    );
  },
);

PublishInsightModal.displayName = 'PublishInsightModal';

export default PublishInsightModal;
